import { Component, ComponentFactoryResolver, OnDestroy, OnInit } from '@angular/core';
import { BroadcastService, EditableConfigGridData, GridBaseComponent, ModalService, ModalSize, ToasterType } from '@cv-invision/core';
import { Store } from '@ngxs/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LenderService } from '../../service/lender.service';
import { SetFNCSettings } from '../../state/actions/global-configuration.actions';
import { GlobalConfigurationState } from '../../state/global-configuration.state';
import { FNCCallCenterClientComponent } from './call-center-clients-form/call-center-client-form';
import { CustomActionCallCenterFNCCellComponent } from './custom-grid-cell/custom-action-callcenter-fnc.components';
import { FNCCallCenterClientGridColumnsModel } from './model/fnc-client-condifg-columm.model';
import { BranchModel, DefaultSettingsModel } from './model/fnc-default-setting.model';
import { FNCDefaultSettingService } from './service/fnc-default-setting.service';
import { OrganizationsService } from '../../service/organization.service';

@Component({
	selector: 'cv-invision-fnc-default-setting',
	templateUrl: './fnc-default-setting.component.html',
	styleUrls: ['./fnc-default-setting.component.scss']
})
export class FNCDefaultSettingComponent extends GridBaseComponent implements OnInit, OnDestroy {
	public saving: boolean;
	public isDirtyForm: boolean;
	public organizationSelected: Array<any> = [];
	public acuityBranchSelected: Array<BranchModel> = [];
	public acuityLenderBranchesList: Array<BranchModel> = this.store.selectSnapshot(GlobalConfigurationState.getAcuityLenderBranches);
	public appScopeLendersList: Array<BranchModel> = this.store.selectSnapshot(GlobalConfigurationState.getAppScopeLenders);
	public clientBranchSelected: Array<BranchModel> = [];
	public defaultConfigModel: DefaultSettingsModel = new DefaultSettingsModel();
	public FNCDefaultSettings: DefaultSettingsModel = new DefaultSettingsModel();
	public optionsReady: boolean = false;
	private atComponentDestroy = new Subject();
	public organizationsList: Array<any> = [];

	constructor(
		private store: Store,
		private broadcastService: BroadcastService,
		private FNCDefaultSettingService: FNCDefaultSettingService,
		public lenderService: LenderService,
		public organizationService: OrganizationsService,
		private modalService: ModalService,
		private componentFactoryResolver: ComponentFactoryResolver,
	) {
		super();
	}

	async ngOnInit(): Promise<void> {
		this.FNCDefaultSettingService.setFormData({ isDirty: true });
		this.gridOptions = {
			rowHeight: 40,
			rowSelection: 'single',
			defaultColDef: {
				resizable: true
			},
			localeText: {
				noRowsToShow: 'No Clients to show',
				loadingOoo: 'Loading Clients...'
			},
			columnDefs: new FNCCallCenterClientGridColumnsModel().getColumns(),
			frameworkComponents: {
				customActionCallCenterFNCCellComponent: CustomActionCallCenterFNCCellComponent
			}
		};

		await this.FNCDefaultSettingService
				  .getFNCDefaultSettings()
				  .toPromise()
				  .then(result => {
						this.FNCDefaultSettings = result;
						this.defaultConfigModel = JSON.parse(JSON.stringify(this.FNCDefaultSettings));
						this.gridData = this.defaultConfigModel.clients;
						if (result.defaultBranchId) {
							this.acuityBranchSelected = this.acuityLenderBranchesList.filter(
								b => b.id === this.defaultConfigModel.defaultBranchId
							);
						}
						if (result.defaultClientId) {
							this.clientBranchSelected = this.appScopeLendersList.filter(
								b => b.id === this.defaultConfigModel.defaultClientId
							);
							if (!this.clientBranchSelected.length) {
								this.onSearchAppScopeLender(this.defaultConfigModel);
							}
						}
						
						if (result.defaultClassOrganizationId) {
							this.organizationSelected = [{
								id: result.defaultClassOrganizationId,
								name: result.defaultClassOrganizationName
							}];
						}
						this.optionsReady = true;
				  });

		// listen to valutrust property types config editor
		this.FNCDefaultSettingService
			.getFormDataListener()
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((result: EditableConfigGridData) => {

				if (result.doSave !== undefined && result.doSave) {
					this.onSave();
				}

				if (result.metadata && result.metadata.reloadList) {
					// get updated info after update, create or delete any client data
					this.FNCDefaultSettings = this.store.selectSnapshot(GlobalConfigurationState.getFNCSettings);
					this.defaultConfigModel = JSON.parse(JSON.stringify(this.FNCDefaultSettings));
					this.loadGridData();
				}

				if (result.isDirty !== undefined) {
					this.isDirtyForm = result.isDirty;
				}

				if (result.saving !== undefined) {
					this.saving = result.saving;
				}
			});

		this.store.dispatch(new SetFNCSettings(this.FNCDefaultSettings));
		this.getOrganizations();
		this.loadGridData();
	}

	loadGridData(): void {
		this.gridData = this.defaultConfigModel.clients;
		this.gridPagination.pagination.totalCount = this.defaultConfigModel.clients.length;
	}

	/**
	 * Call service endpoint to search organizations.
	 */
	private getOrganizations(): void {
		this.organizationService
			.getOrganizationsByName()
			.subscribe(
				result => {
					if (result) {
						this.organizationsList = result;
					}
				},
				error => console.error(error)
			);
	}

	/**
	 * On save search client appscope.
	 */
	onSearchAppScopeLender(configInf: any): void {
		this.lenderService
			.getLenders(configInf.defaultClientName)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(r => {
				this.clientBranchSelected = r.filter(b => b.id === configInf.defaultClientId);
				this.optionsReady = true;
			});
	}

	/**
	 * On save grid config.
	 */
	onSave(): void {
		this.FNCDefaultSettingService
			.saveFNCDefaultSettings(this.defaultConfigModel)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(
				result => {
					if (result && result.succeeded) {
						this.broadcastService.broadcast({
							name: ToasterType.SUCCESS,
							event: { message: 'Default Settings saved successfully' }
						});
					}
					this.FNCDefaultSettings = JSON.parse(JSON.stringify(this.defaultConfigModel));
					this.store.dispatch(new SetFNCSettings(this.FNCDefaultSettings));
					this.FNCDefaultSettingService.setFormData({ saving: false, doSave: false });
				},
				error => {
					console.error(error);
					this.broadcastService.broadcast({
						name: ToasterType.ERROR,
						event: { error: 'Error occurred ' + error }
					});
					this.FNCDefaultSettingService.setFormData({ saving: false, doSave: false });;
				}
			);
	}

	/**
	 *call add client modal form
	 */
	addClient(): void {
		this.modalService
			.open({
				componentFactoryResolver: this.componentFactoryResolver,
				component: FNCCallCenterClientComponent,
				data: {defualtConfig: this.defaultConfigModel, isEditing: false},
				modalConfiguration: {
					title: `Add FNC client configuration`,
					modalSize: ModalSize.MD
				}
			})
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				if (result.success) {
					this.FNCDefaultSettingService.setFormData({ metadata:{reloadList:true} });
				}
			});
	}

	/**
	 * on branch selected
	 * @param $event
	 */
	onBranchSelected($event: any, type: string): void {
		if ($event && type === 'classOrganization') {
			this.defaultConfigModel.defaultClassOrganizationId = $event.id;
			this.defaultConfigModel.defaultClassOrganizationName = $event.name;
		}

		if ($event && type === 'acuityBranch') {
			this.defaultConfigModel.defaultBranchId = $event.id;
			this.defaultConfigModel.defaultBranchName = $event.title;
		}
		if ($event && type === 'appscopeClient') {
			this.defaultConfigModel.defaultClientId = $event.id;
			this.defaultConfigModel.defaultClientName = $event.title;
		}
		if (!$event && type === 'acuityBranch') {
			this.defaultConfigModel.defaultBranchId = null;
			this.defaultConfigModel.defaultBranchName = null;
		}
		if (!$event && type === 'appscopeClient') {
			this.defaultConfigModel.defaultClientId = null;
			this.defaultConfigModel.defaultClientName = null;
		}
	}

	/**
	 * Save margin cap
	 * @param $event 
	 */
	onChangeMarginCap($event: any): void {
		if ($event.target) {
			this.defaultConfigModel.defaultMarginCap = $event.target.value < 0 ? 0 : $event.target.value;	
		} else {
			this.defaultConfigModel.defaultMarginCap = $event.value < 0 ? 0 : $event.value;
		}
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
